import { Ref } from "vue";
import { EventPriceDto } from "@/models.g";
import { TicketPurchaseDtoViewModel } from "@/viewmodels.g";

export function useTicketChange(
  purchase: TicketPurchaseDtoViewModel,
  localQuantity?: Ref<number> | null,
  pendingQuantity?: Ref<number | null> | null,
) {
  // Get max quantity for a ticket

  function handleQuantityUpdate(val: number, minimumQuantity?: number | null) {
    const min = minimumQuantity ?? 0;
    let adjustedQuantity = val;
    if (adjustedQuantity > 0 && adjustedQuantity < min) {
      adjustedQuantity = min;
    }
    if (localQuantity) localQuantity.value = adjustedQuantity;
    if (pendingQuantity) pendingQuantity.value = adjustedQuantity;
  }

  const getEventPrice = computed(
    () =>
      (eventPriceId: number): EventPriceDto => {
        const eventDate = purchase.eventDate;
        return eventDate!.eventPrices!.find((p) => p.id === eventPriceId)!;
      },
  );

  const getMaximumPurchaseQuantity = computed(
    () =>
      (eventPriceId: number): number => {
        const maxPurchaseQuantity =
          getEventPrice.value(eventPriceId)?.maximumPurchaseQuantity;
        if (!maxPurchaseQuantity) {
          return 999999;
        }
        return maxPurchaseQuantity;
      },
  );

  const getMaxQuantity = computed(() => (eventPriceId: number): number => {
    const eventDate = purchase.eventDate;
    const eventPrice = getEventPrice.value(eventPriceId);
    // Use availableSeats from eventDate, which excludes sold/restricted seats
    const availableSeats = eventDate?.availableSeats ?? 9999;
    // If the price has a max purchase quantity, use the lower of availableSeats and maxPurchaseQuantity
    if (eventPrice?.maximumPurchaseQuantity) {
      return Math.min(availableSeats, eventPrice.maximumPurchaseQuantity);
    }
    return availableSeats;
  });

  return {
    getMaxQuantity,
    handleQuantityUpdate,
    getMaximumPurchaseQuantity,
    getEventPrice,
  };
}
